import React, { useState, useEffect, useRef, useCallback } from "react";
import { Save, X, AlertTriangle, Camera } from "lucide-react";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import {
  db,
  storage,
  getSocialFeeds,
  deleteSocialFeed,
} from "../../services/firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { validateImageFile } from "../utils/imageValidation";
import Card from "../Card/Card";
import { FeedType } from "../../types/socialFeed";
import { isDuplicateUrl } from "../utils/duplicateURL";

// Types
interface SocialFeedProps {
  onSaveSuccess?: () => void;
}

const SocialFeed: React.FC<SocialFeedProps> = ({ onSaveSuccess }) => {
  const [url, setUrl] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [socialFeeds, setSocialFeeds] = useState<FeedType[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");

  useEffect(() => {
    // Fetching socialFeeds and storing in desc order to show latest created at top of grid
    (async () => {
      try {
        setLoading(true);
        const feedList = await getSocialFeeds();
        setSocialFeeds(feedList);
      } catch (err) {
        console.error("Failed to fetch socialFeeds:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const validateFeed = useCallback(() => {
    if (!url.trim()) {
      setError("URL is required");
      return false;
    }
    const urlRegex = /^(https?:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;
    if (!urlRegex.test(url.trim())) {
      setError("Invalid URL format");
      return false;
    }
    if (!selectedImage) {
      setError("Image is required");
      return false;
    }
    setError(null);
    return true;
  }, [url, selectedImage]);

  const handleImageChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      const validationError = validateImageFile(file);
      if (validationError) {
        setError(validationError);
        return;
      }

      if (file) {
        setSelectedImage(file);
        setImagePreview(URL.createObjectURL(file));
        setError(null);
      }
    },
    []
  );

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const handleDelete = useCallback(async (feedId: string) => {
    try {
      await deleteSocialFeed(feedId);
      setSocialFeeds((prev) => prev.filter((feed) => feed.id !== feedId));
    } catch (error) {
      console.error("Failed to delete feed:", error);
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!validateFeed()) return;

    setIsSaving(true);
    try {
      const isDuplicate = await isDuplicateUrl("social_feed", url);
      if (isDuplicate) {
        setError("Duplicate entry detected.");
        setIsSaving(false);
        return;
      }

      let imageUrl = null;
      if (selectedImage) {
        const storageRef = ref(
          storage,
          `social_feed/${Date.now()}_${selectedImage.name}`
        );
        await uploadBytes(storageRef, selectedImage);
        imageUrl = await getDownloadURL(storageRef);
      }

      const feedData = {
        image: imageUrl,
        url: url.trim(),
        title: title,
      };

      const docRef = await addDoc(collection(db, "social_feed"), {
        ...feedData,
        createdAt: serverTimestamp(),
      });

      setSocialFeeds((prev) => [{ id: docRef.id, ...feedData }, ...prev]);
      onSaveSuccess?.();
      setTitle("");
      setSelectedImage(null);
      setImagePreview(null);
      setUrl("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      setError(null);
    } catch (error) {
      console.error("Error saving feed:", error);
      setError("Failed to save feed. Please try again.");
    } finally {
      setIsSaving(false);
    }
  }, [selectedImage, title, url, validateFeed, onSaveSuccess]);

  return (
    <>
      <div className="max-w-6xl mx-auto p-6 space-y-8">
        <div className="bg-white rounded-lg shadow p-6 space-y-4">
          <h2 className="text-2xl font-bold mb-4">Social Feed</h2>

          {error && (
            <div className="flex items-center gap-2 bg-red-50 border-l-4 border-red-500 p-4 mb-4">
              <AlertTriangle className="text-red-500" />
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium mb-2">Link</label>
              <input
                type="text"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError(null);
                }}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
                placeholder="https://your-social-feed.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Social Feed Image
              </label>
              <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md">
                <div className="space-y-2 text-center">
                  <div className="flex flex-col items-center">
                    {imagePreview ? (
                      <div className="relative">
                        <img
                          src={imagePreview}
                          alt="Preview"
                          className="max-h-48 rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <Camera size={48} className="mx-auto text-gray-400" />
                    )}
                  </div>

                  <label
                    htmlFor="image-upload"
                    className="relative cursor-pointer rounded-md font-medium text-[#18f7b1] hover:text-[#0fd494]"
                  >
                    {imagePreview ? "Change image" : "Upload an image"}
                    <input
                      id="image-upload"
                      ref={fileInputRef}
                      type="file"
                      className="sr-only"
                      accept="image/*"
                      onChange={handleImageChange}
                    />
                  </label>

                  <p className="text-sm text-gray-500">
                    PNG, JPG, JPEG, WEBP up to 10MB
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setError(null);
                }}
                className="w-full p-2 border rounded-md focus:ring-2 focus:ring-[#18f7b1]"
                placeholder="Enter feed title"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className={`px-6 py-3 bg-[#18f7b1] text-black rounded-lg hover:bg-[#0fd494] flex items-center gap-2 transition-all duration-200 ${
              isSaving ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-black"></div>
                Publishing...
              </>
            ) : (
              <>
                <Save size={20} />
                Publish
              </>
            )}
          </button>
        </div>
      </div>

      <div className="mt-8 max-w-4xl mx-auto">
        <h3 className="text-xl font-semibold mb-4 text-center">
          Feeds to be seen in Homepage
        </h3>

        {loading ? (
          <div className="flex justify-center items-center h-40">
            <div className="animate-spin h-8 w-8 border-4 border-[#18f7b1] border-t-transparent rounded-full"></div>
          </div>
        ) : socialFeeds.length === 0 ? (
          <p className="text-center text-gray-500">No feeds saved yet.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 overflow-y-auto max-h-[450px]">
            {socialFeeds.map((feed) => (
              <Card
                key={feed.id}
                title={feed.title}
                subtitle={
                  <a href={feed.url} target="_blank" rel="noopener noreferrer">
                    {feed.url}
                  </a>
                }
                imageUrl={feed.image}
                deleteModalProps={{
                  onConfirm: () => handleDelete(feed.id),
                  description: "Are you sure you want to delete this feed?",
                  isDeleting: loading,
                //buttonText: "Delete",
                }}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(SocialFeed);
